import { useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Group,
  NumberInput,
  Paper,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { formatPrice } from "@livecommerce/ui";
import { useTranslation } from "react-i18next";
import { ApiError } from "../api/client";
import type { CatalogueEntry, Settings } from "../catalogue/api";
import { suggestedQuantity } from "../catalogue/search";
import { type AuctionSettings, type StockCheck, shows } from "./api";
import { AuctionFields } from "./AuctionFields";
import { priceProblem } from "./price";
import {
  SELL_SECONDS_MAX,
  SELL_SECONDS_MIN,
  type SellInput,
  sellSecondsInvalid,
  sellSecondsOf,
} from "./sale";
import { DEFAULT_AUCTION, DEFAULT_AUCTION_PRICE } from "./auction";
import styles from "../catalogue/Catalogue.module.scss";

/**
 * What to do with a product picked from the catalogue (M1.13 §3, §7).
 *
 * Two modes, one form, because the choices are the same and only the moment differs:
 *
 * - **`feature`** — the show is live. Pressing *Feature* (or Enter) checks Shopify's stock and puts
 *   the product in front of the room, creating its lot as it does (`POST …/feature`).
 * - **`shortlist`** — the show has not started. *Add to shortlist* prepares the product for later
 *   and holds no stock (`POST …/lots`).
 *
 * **An auction offers one unit**, always, and has no quantity to set — each auction sells one item
 * to one winner, and selling another is featuring the product again (*Run again* on the lot).
 * **A fixed-price lot's quantity starts at the default share** (D6) of the stock the catalogue knows about, and
 * the host may change it; the price is blank for Shopify's. So an operator mid-show can pick a
 * product and press Enter and get the sensible lot. The quantity is **checked against Shopify when
 * the lot is featured and never adjusted to fit** — a lot that offers more than Shopify has is
 * refused, and says how many there are.
 *
 * Refusals are said in the words the server chose, with the stock figures it decided on beside
 * them, because *below the minimum* and *Shopify did not answer* ask for different things next:
 * pick another product, or press again.
 */
export function ProductSheet({
  showId,
  entry,
  mode,
  settings,
  onDone,
}: {
  showId: string;
  entry: CatalogueEntry;
  mode: "feature" | "shortlist";
  settings: Settings | undefined;
  /** Called after a success, with the lot's id, so the page can re-read the show. */
  onDone: () => Promise<void>;
}) {
  const { t } = useTranslation();
  // Auction by default: it is how this platform's shows mostly sell. Fixed price is one tap away.
  const [saleMode, setSaleMode] = useState<"fixed" | "auction">("auction");
  const [price, setPrice] = useState("");
  const share = settings?.featureSharePercent ?? 50;
  const [quantity, setQuantity] = useState<number | "">(() => {
    const suggested = suggestedQuantity(entry.available, share);
    return suggested > 0 ? suggested : "";
  });
  const [auction, setAuction] = useState<AuctionSettings>(DEFAULT_AUCTION);
  /** `R-LOT-33` — the room's *X left*, for a fixed-price lot. Off unless the host asks. */
  const [showRemaining, setShowRemaining] = useState(false);
  /** `R-LOT-34` — the sale's time limit in seconds; empty for none, which is the default. */
  const [sellSeconds, setSellSeconds] = useState<SellInput>("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [stock, setStock] = useState<StockCheck | undefined>(undefined);

  const isAuction = saleMode === "auction";
  const sellInvalid = !isAuction && sellSecondsInvalid(sellSeconds);
  // An auction's default floor is £1 rather than the shop price — the same rule the lot row
  // follows — and only when the operator has not typed one.
  const defaultPrice = isAuction ? DEFAULT_AUCTION_PRICE : entry.price;
  // `R-LOT-32` — only a typed price; a blank one is Shopify's, which the server judges.
  const problem = price.trim() ? priceProblem(price, saleMode) : undefined;

  async function submit() {
    setBusy(true);
    setError(undefined);
    setStock(undefined);
    const typedPrice = price.trim();
    const choices = {
      saleMode,
      ...(typedPrice
        ? { price: typedPrice }
        : isAuction
          ? { price: DEFAULT_AUCTION_PRICE }
          : {}),
      ...(isAuction
        ? { auction }
        : { showRemaining, sellSeconds: sellSecondsOf(sellSeconds) }),
    };
    try {
      if (mode === "feature") {
        await shows.feature(
          showId,
          { variantId: entry.variantId },
          {
            ...choices,
            ...(isAuction ? { quantity: 1 } : quantity !== "" ? { quantity } : {}),
          },
        );
      } else {
        await shows.addLot(showId, {
          variantId: entry.variantId,
          ...choices,
          ...(isAuction
            ? { allocated: 1 }
            : quantity !== ""
              ? { allocated: quantity }
              : {}),
        });
      }
      await onDone();
    } catch (failure) {
      if (failure instanceof ApiError) {
        setError(failure.message);
        const reported = failure.body.stock as StockCheck | undefined;
        if (reported) setStock(reported);
      } else {
        setError(t("shows.unreachable"));
      }
    } finally {
      setBusy(false);
    }
  }

  // The server refuses all three; saying so here saves the operator a press that cannot work.
  if (!entry.sku?.trim() || !entry.tracked || !entry.sellable) {
    return (
      <Alert color="orange" variant="light" radius="md">
        {t(
          !entry.sku?.trim()
            ? "catalogue.disabledHelp"
            : entry.tracked
              ? "catalogue.noLocationHelp"
              : "catalogue.untrackedHelp",
        )}
      </Alert>
    );
  }

  return (
    <Paper withBorder radius="md" p="sm" className={styles.card}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!busy) void submit();
        }}
      >
        <Stack gap="xs">
          <Text size="sm" fw={600}>
            {entry.variantTitle
              ? `${entry.productTitle} — ${entry.variantTitle}`
              : entry.productTitle}
          </Text>
          <Text size="xs" c="dimmed">
            {t("catalogue.sheetMeta", {
              price: formatPrice(entry.price),
              available: entry.available ?? 0,
            })}
            {entry.breakEven ? (
              <>
                {" · "}
                <Text component="span" size="xs" fw={700}>
                  {t("lots.breakEven", { amount: formatPrice(entry.breakEven) })}
                </Text>
              </>
            ) : null}
          </Text>

          <SegmentedControl
            size="xs"
            value={saleMode}
            onChange={(value) => setSaleMode(value as "fixed" | "auction")}
            disabled={busy}
            data={[
              { value: "fixed", label: t("lots.mode.fixed") },
              { value: "auction", label: t("lots.mode.auction") },
            ]}
          />

          <Group gap="sm" align="flex-end" wrap="wrap">
            <TextInput
              size="xs"
              label={t(isAuction ? "lots.startingPriceLabel" : "lots.priceLabel")}
              placeholder={defaultPrice}
              description={t("catalogue.priceDefault")}
              value={price}
              onChange={(event) => setPrice(event.currentTarget.value)}
              disabled={busy}
              error={problem !== undefined}
            />
            {isAuction ? null : (
              <NumberInput
                size="xs"
                label={t("lots.quantityLabel")}
                description={t("catalogue.quantityDefault", { share })}
                value={quantity}
                min={1}
                allowDecimal={false}
                onChange={(value) => setQuantity(value === "" ? "" : Number(value))}
                disabled={busy}
              />
            )}
            <Button
              type="submit"
              size="xs"
              loading={busy}
              disabled={
                problem !== undefined || sellInvalid || (!isAuction && quantity === "")
              }
            >
              {t(mode === "feature" ? "catalogue.feature" : "catalogue.shortlist")}
            </Button>
          </Group>
          {/* Under the row rather than under the field, so the field stays in line (see `LotRow`). */}
          {problem ? (
            <Text size="xs" c="red">
              {t(`lots.priceProblem.${problem}`)}
            </Text>
          ) : null}

          {isAuction ? (
            <AuctionFields value={auction} onChange={setAuction} disabled={busy} />
          ) : (
            <Stack gap="xs">
              <NumberInput
                size="xs"
                label={t("lots.sellSecondsLabel")}
                description={t("lots.sellSecondsHelp")}
                placeholder={t("lots.sellSecondsNone")}
                value={sellSeconds}
                min={SELL_SECONDS_MIN}
                max={SELL_SECONDS_MAX}
                suffix="s"
                allowDecimal={false}
                allowNegative={false}
                onChange={(value) => setSellSeconds(value === "" ? "" : Number(value))}
                error={sellInvalid ? t("lots.sellSecondsInvalid") : undefined}
                disabled={busy}
              />
              <Checkbox
                size="xs"
                label={t("lots.showRemaining")}
                description={t("lots.showRemainingHelp")}
                checked={showRemaining}
                onChange={(event) => setShowRemaining(event.currentTarget.checked)}
                disabled={busy}
              />
            </Stack>
          )}

          {error ? (
            <Alert
              color="red"
              variant="light"
              radius="md"
              title={t(
                mode === "feature"
                  ? "lots.refused.title"
                  : "lots.refused.shortlistTitle",
              )}
            >
              <Stack gap={2}>
                <Text size="sm">{error}</Text>
                {stock ? (
                  <Text size="xs" c="dimmed">
                    {t("catalogue.stockCheck", {
                      available: stock.available,
                      unheld: stock.unheld,
                      effective: stock.effective,
                      minimum: stock.minimum,
                    })}
                  </Text>
                ) : null}
              </Stack>
            </Alert>
          ) : null}
        </Stack>
      </form>
    </Paper>
  );
}
